import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Icon, Message, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import { DropdownItemProps } from 'semantic-ui-react';
import { getBondInsuranceByID, updateBondInsuranceLenderInsurance } from '../../../services/bond-insurance';
import { successNotification, dangerNotification } from '../../../services/notification';
import { calculateAge } from '../../../utils/calculateAge';
import { extractApiErrorMessages } from '../../../utils/apiErrorHandler';
import {
  BondInsuranceLike,
  getBondNaturalTenant,
  getBondTenantBirthDate,
  getBondTenantGender,
  getBondTenantMaritalStatus,
  getMissingBondTenantProfileFields,
  isBondTenantProfileComplete
} from '../../../utils/bondInsuranceTenantProfile';
import { genderOptions, maritalStatusOptions } from '../../bond-insurance-budget-form/types';

const InfoSection = styled.div`
  background: linear-gradient(135deg, #f8f5fc 0%, #fff 100%);
  border-radius: 12px;
  padding: 24px;
  margin: 16px 0;
  border: 1px solid #e0e0e0;
`;

const ProductBadge = styled.span`
  background: #59249B;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 12px;
`;

const ValueDisplay = styled.div`
  text-align: center;
  margin: 20px 0;
`;

const ValueLabel = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
`;

const ValueAmount = styled.p`
  color: #59249B;
  font-size: 32px;
  font-weight: 700;
  margin: 0;
`;

const ValuePeriod = styled.span`
  color: #666;
  font-size: 16px;
  font-weight: 400;
`;

const ConditionsLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #59249B;
  font-size: 14px;
  text-decoration: none;
  padding: 12px;
  background: #f8f5fc;
  border-radius: 8px;
  margin-top: 16px;
  transition: all 0.2s ease;

  &:hover {
    background: #ede5f5;
    text-decoration: underline;
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
  font-size: 14px;
  margin-bottom: 8px;

  .icon {
    color: #59249B;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #666;
  font-size: 13px;
`;

const InfoValue = styled.span`
  color: #333;
  font-size: 13px;
  font-weight: 500;
`;

const ProfileLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 0;
  color: #666;
  font-size: 14px;
`;

interface LenderInsuranceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bondInsurance: BondInsuranceLike & { id: string };
  rate?: number;
  tenantName?: string;
  propertyAddress?: string;
  isTenantPF?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const getOptionLabel = (
  value: string | undefined,
  options: DropdownItemProps[]
): string => {
  if (!value) return '-';
  const option = options.find(item => item.value === value);
  return typeof option?.text === 'string' ? option.text : value;
};

const LenderInsuranceModal: React.FC<LenderInsuranceModalProps> = ({
  open,
  onClose,
  onSuccess,
  bondInsurance,
  rate,
  tenantName: tenantNameProp,
  propertyAddress: propertyAddressProp,
  isTenantPF = true
}) => {
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [resolvedBondInsurance, setResolvedBondInsurance] = useState<BondInsuranceLike | null>(null);

  const activeBondInsurance = resolvedBondInsurance || bondInsurance;
  const existingId = activeBondInsurance?.lenderInsuranceId || activeBondInsurance?.lender_insurance_id;

  const tenantName = tenantNameProp ||
    getBondNaturalTenant(activeBondInsurance)?.main_tenant_name ||
    activeBondInsurance?.legal_tenant?.company ||
    activeBondInsurance?.legalTenant?.company ||
    'N/A';

  const propertyAddress = propertyAddressProp ||
    (activeBondInsurance?.property
      ? `${activeBondInsurance.property.street}, ${activeBondInsurance.property.number}`
      : 'N/A');

  const tenantGender = getBondTenantGender(activeBondInsurance);
  const tenantMaritalStatus = getBondTenantMaritalStatus(activeBondInsurance);
  const tenantBirthDate = getBondTenantBirthDate(activeBondInsurance);
  const missingProfileFields = getMissingBondTenantProfileFields(activeBondInsurance);
  const isProfileComplete = isBondTenantProfileComplete(activeBondInsurance);

  const tenantAge = useMemo(() => {
    if (!tenantBirthDate) return null;
    return calculateAge(tenantBirthDate);
  }, [tenantBirthDate]);

  const isAgeEligible = tenantAge === null || tenantAge < 65;

  const loadBondInsuranceProfile = useCallback(async () => {
    if (!bondInsurance?.id) return;

    setProfileLoading(true);
    setProfileError(null);

    try {
      const fullBondInsurance = await getBondInsuranceByID(bondInsurance.id);
      setResolvedBondInsurance(fullBondInsurance);
    } catch (error) {
      setProfileError(
        extractApiErrorMessages(error, 'Não foi possível carregar os dados do locatário.')
      );
      setResolvedBondInsurance(null);
    } finally {
      setProfileLoading(false);
    }
  }, [bondInsurance?.id]);

  useEffect(() => {
    if (!open) {
      setResolvedBondInsurance(null);
      setProfileError(null);
      setProfileLoading(false);
      return;
    }

    if (isBondTenantProfileComplete(bondInsurance)) {
      setResolvedBondInsurance(bondInsurance);
      setProfileError(null);
      setProfileLoading(false);
      return;
    }

    loadBondInsuranceProfile();
  }, [open, bondInsurance, loadBondInsuranceProfile]);

  const canAddInsurance =
    isTenantPF &&
    !profileLoading &&
    !profileError &&
    isProfileComplete &&
    isAgeEligible;

  const handleAction = async (adding: boolean) => {
    if (adding && !canAddInsurance) {
      return;
    }

    setLoading(true);
    try {
      await updateBondInsuranceLenderInsurance(bondInsurance.id, adding);
      successNotification(
        'Sucesso',
        `Seguro Prestamista ${adding ? 'adicionado' : 'removido'} com sucesso.`
      );
      onSuccess();
      onClose();
    } catch (error) {
      dangerNotification(
        'Erro',
        extractApiErrorMessages(error, 'Não foi possível atualizar o seguro prestamista.')
      );
    } finally {
      setLoading(false);
    }
  };

  const renderProfileStatus = () => {
    if (existingId || !isTenantPF) {
      return null;
    }

    if (profileLoading) {
      return (
        <ProfileLoader>
          <Loader active inline size="small" />
          Carregando dados do locatário...
        </ProfileLoader>
      );
    }

    if (profileError) {
      return (
        <Message negative icon>
          <Icon name="warning circle" />
          <Message.Content>
            <Message.Header>Não foi possível carregar os dados do locatário</Message.Header>
            {profileError}
          </Message.Content>
        </Message>
      );
    }

    if (!isProfileComplete) {
      return (
        <Message warning icon>
          <Icon name="exclamation triangle" />
          <Message.Content>
            <Message.Header>Dados incompletos para contratar o prestamista</Message.Header>
            {`Complete no cadastro da fiança: ${missingProfileFields.join(', ')}.`}
          </Message.Content>
        </Message>
      );
    }

    if (!isAgeEligible) {
      return (
        <Message warning icon>
          <Icon name="exclamation triangle" />
          <Message.Content>
            Idade limite atingida: Produto válido apenas para clientes com menos de 65 anos.
          </Message.Content>
        </Message>
      );
    }

    return null;
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <Icon name="shield" color="purple" /> Seguro Prestamista
        <ProductBadge style={{ marginLeft: 12 }}>HDI Seguros</ProductBadge>
      </Modal.Header>

      <Modal.Content>
        {existingId ? (
          <>
            <Message warning icon>
              <Icon name="exclamation triangle" />
              <Message.Content>
                <Message.Header>Seguro Prestamista Ativo</Message.Header>
                Este seguro fiança já possui o Seguro Prestamista incluído. Deseja removê-lo?
              </Message.Content>
            </Message>

            <InfoSection>
              <InfoRow>
                <InfoLabel>Locatário:</InfoLabel>
                <InfoValue>{tenantName}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Endereço:</InfoLabel>
                <InfoValue>{propertyAddress}</InfoValue>
              </InfoRow>
              <ValueDisplay>
                <ValueLabel>Valor que será removido da cobrança:</ValueLabel>
                <ValueAmount>
                  {rate != null && rate !== undefined ? formatCurrency(rate) : 'N/A'}
                  <ValuePeriod>/mês</ValuePeriod>
                </ValueAmount>
              </ValueDisplay>
            </InfoSection>
          </>
        ) : (
          <>
            {!isTenantPF && (
              <Message warning icon>
                <Icon name="exclamation triangle" />
                <Message.Content>
                  Seguro Prestamista disponível apenas para locatários Pessoa Física (CPF).
                </Message.Content>
              </Message>
            )}

            {renderProfileStatus()}

            <InfoSection>
              <ProductBadge>Proteção Completa</ProductBadge>

              <p style={{ color: '#333', marginBottom: 16 }}>
                O <strong>Seguro Prestamista HDI</strong> garante o pagamento do aluguel
                em caso de imprevistos, protegendo o inquilino e o proprietário.
              </p>

              <BenefitsList>
                <BenefitItem>
                  <Icon name="check circle" className="icon" />
                  Cobertura em caso de morte
                </BenefitItem>
                <BenefitItem>
                  <Icon name="check circle" className="icon" />
                  Cobertura em caso de invalidez permanente
                </BenefitItem>
                <BenefitItem>
                  <Icon name="check circle" className="icon" />
                  Proteção contra desemprego involuntário
                </BenefitItem>
              </BenefitsList>

              <InfoRow>
                <InfoLabel>Locatário:</InfoLabel>
                <InfoValue>{tenantName}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Endereço:</InfoLabel>
                <InfoValue>{propertyAddress}</InfoValue>
              </InfoRow>
              {isProfileComplete && (
                <>
                  <InfoRow>
                    <InfoLabel>Sexo:</InfoLabel>
                    <InfoValue>{getOptionLabel(tenantGender, genderOptions)}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Estado Civil:</InfoLabel>
                    <InfoValue>{getOptionLabel(tenantMaritalStatus, maritalStatusOptions)}</InfoValue>
                  </InfoRow>
                </>
              )}

              <ValueDisplay>
                <ValueLabel>Valor mensal:</ValueLabel>
                <ValueAmount>
                  {rate != null && rate !== undefined ? formatCurrency(rate) : 'N/A'}
                  <ValuePeriod>/mês</ValuePeriod>
                </ValueAmount>
              </ValueDisplay>
            </InfoSection>

            <ConditionsLink
              href="/documents/prestamista-hdi.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="file pdf outline" />
              Consultar Condições Gerais do Produto
            </ConditionsLink>
          </>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        {profileError && !existingId && isTenantPF && (
          <Button onClick={loadBondInsuranceProfile} disabled={profileLoading || loading}>
            Tentar novamente
          </Button>
        )}
        {!existingId && isTenantPF && (
          <Button
            primary
            color="purple"
            onClick={() => handleAction(true)}
            loading={loading}
            disabled={!canAddInsurance || loading}
          >
            <Icon name="check" /> Adicionar Seguro
          </Button>
        )}
        {existingId && (
          <Button
            color="red"
            onClick={() => handleAction(false)}
            loading={loading}
          >
            <Icon name="trash" /> Remover Seguro
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default LenderInsuranceModal;
